// src/components/EditInstallationInvoiceModal.jsx
import React, { useState } from 'react';
import api from '../services/api';
import { X, Loader, Plus, Trash2 } from 'lucide-react';

const OWNERSHIP_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'company', label: 'Company' },
];

const EditInstallationInvoiceModal = ({ invoice, onClose, onSave }) => {
  const [notes, setNotes] = useState(invoice.notes || '');
  const [items, setItems] = useState(
    invoice.items?.map(item => ({
      description: item.description,
      actual_amount: String(item.actual_amount),
      discount_amount: String(item.discount_amount || '0'),
      ownership: item.ownership,
    })) || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems(prev => [...prev, { description: '', actual_amount: '', discount_amount: '0', ownership: 'user' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculatePayable = (item) => {
    const actual = parseFloat(item.actual_amount) || 0;
    const discount = parseFloat(item.discount_amount) || 0;
    return Math.max(0, actual - discount);
  };

  const totalPayable = items.reduce((sum, item) => sum + calculatePayable(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = items.filter(item => item.description && item.actual_amount);
    if (validItems.length === 0) {
      setError('At least one item with a description and amount is required.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await api.patch(`/installation-invoices/${invoice.id}/`, {
        notes,
        items: validItems.map(item => ({
          description: item.description,
          actual_amount: parseFloat(item.actual_amount),
          discount_amount: parseFloat(item.discount_amount) || 0,
          payable_amount: calculatePayable(item),
          ownership: item.ownership,
        })),
      });
      onSave(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.[0] || 'Failed to update invoice.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-5 py-3.5 flex items-center justify-between rounded-t-xl z-10">
          <div>
            <h2 className="text-base font-bold text-gray-900">Edit Installation Invoice</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Invoice #{invoice.id} · {invoice.customer_name}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Installation Items</h3>
              <button type="button" onClick={addItem}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                <Plus size={14} /> Add Item
              </button>
            </div>

            <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase mb-1 px-1">
              <div className="col-span-4">Description</div>
              <div className="col-span-2">Actual (Rs.)</div>
              <div className="col-span-2">Discount (Rs.)</div>
              <div className="col-span-2">Payable</div>
              <div className="col-span-1">Owner</div>
              <div className="col-span-1"></div>
            </div>

            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-gray-50 p-2.5 rounded-lg">
                  <div className="md:col-span-4">
                    <input value={item.description}
                      onChange={e => handleItemChange(index, 'description', e.target.value)}
                      className={inputClass} placeholder="e.g., Router, Wire, Labor" />
                  </div>
                  <div className="md:col-span-2">
                    <input type="number" value={item.actual_amount}
                      onChange={e => handleItemChange(index, 'actual_amount', e.target.value)}
                      className={inputClass} placeholder="0" min="0" />
                  </div>
                  <div className="md:col-span-2">
                    <input type="number" value={item.discount_amount}
                      onChange={e => handleItemChange(index, 'discount_amount', e.target.value)}
                      className={inputClass} placeholder="0" min="0" />
                  </div>
                  <div className="md:col-span-2">
                    <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
                      Rs. {calculatePayable(item).toLocaleString()}
                    </div>
                  </div>
                  <div className="md:col-span-1">
                    <select value={item.ownership}
                      onChange={e => handleItemChange(index, 'ownership', e.target.value)}
                      className={inputClass}>
                      {OWNERSHIP_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-1 flex justify-center">
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-full">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 flex justify-end">
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-5 py-2">
                <span className="text-xs text-purple-700">Total: </span>
                <span className="text-base font-bold text-purple-900">Rs. {totalPayable.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              rows={2} className={inputClass} placeholder="Any additional notes..." />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t">
            <button type="button" onClick={onClose}
              className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
              {loading && <Loader size={14} className="animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInstallationInvoiceModal;
